import React, { useEffect, useMemo, useState } from "react";
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import cineWordMovies from "../data/cineWordMovies";
import "./CineWord.css";

const MAX_GUESSES = 6;
const START_DATE_UTC = Date.UTC(2026, 0, 1);

function normaliseTitle(value = "") {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function getUtcDateKey(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

function getDailyMovie(date = new Date()) {
  const todayUtc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  );

  const dayNumber = Math.floor(
    (todayUtc - START_DATE_UTC) / 86400000
  );

  const safeIndex =
    ((dayNumber % cineWordMovies.length) + cineWordMovies.length) %
    cineWordMovies.length;

  return {
    ...cineWordMovies[safeIndex],
    puzzleNumber: dayNumber + 1,
  };
}

function escapeRegExp(value = "") {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function hideMovieTitle(text, movieTitle) {
  if (!text) return "";

  const titleWords = movieTitle
    .split(/\s+/)
    .map((word) => word.replace(/[^A-Za-z0-9À-ž]/g, ""))
    .filter((word) => word.length > 2);

  return titleWords.reduce((hiddenText, word) => {
    return hiddenText.replace(
      new RegExp(`\\b${escapeRegExp(word)}\\b`, "gi"),
      "••••"
    );
  }, text);
}

function getInitials(value = "") {
  if (!value || value === "N/A") return "";

  return value
    .split(/\s+/)
    .map((part) => part.match(/[A-Za-zÀ-ž]/)?.[0]?.toUpperCase())
    .filter(Boolean)
    .join(".");
}

function getLeadActorHint(actors = "") {
  if (!actors || actors === "N/A") {
    return "Its leading performer remains under wraps";
  }

  const leadActor = actors.split(",")[0]?.trim();
  const initials = getInitials(leadActor);

  return initials
    ? `One of its leading performers has the initials ${initials}`
    : "Its leading performer remains under wraps";
}

function getDirectorHint(director = "") {
  if (!director || director === "N/A") {
    return "Its director remains under wraps";
  }

  const firstDirector = director.split(",")[0]?.trim();
  const initials = getInitials(firstDirector);

  return initials
    ? `The director's initials are ${initials}`
    : "Its director remains under wraps";
}

function buildHints(movie, details) {
  const decade = `${Math.floor(movie.year / 10) * 10}s`;

  if (!details) {
    return [
      `This film was released in the ${decade}`,
      "Its genre is still being prepared",
      "Its filmmaker clue is still being prepared",
      "Its story clue is still being prepared",
      `It was released in ${movie.year}`,
    ];
  }

  const genre =
    details.Genre && details.Genre !== "N/A"
      ? details.Genre
      : "Genre unavailable";

  const runtime =
    details.Runtime && details.Runtime !== "N/A"
      ? details.Runtime
      : "an unknown runtime";

  const country =
    details.Country && details.Country !== "N/A"
      ? details.Country
      : "an unknown country";

  const plot =
    details.Plot && details.Plot !== "N/A"
      ? hideMovieTitle(details.Plot, movie.title)
      : "The story follows characters whose lives are changed by an unexpected conflict.";

  return [
    `It blends these genres: ${genre}`,
    `It is a ${country} production with a runtime of ${runtime}`,
    getDirectorHint(details.Director),
    getLeadActorHint(details.Actors),
    `Story clue: ${plot}`,
  ];
}

export default function CineWord({ user }) {
  const dailyMovie = useMemo(() => getDailyMovie(), []);
  const dateKey = useMemo(() => getUtcDateKey(), []);

  const [guessInput, setGuessInput] = useState("");
  const [guesses, setGuesses] = useState([]);
  const [finished, setFinished] = useState(false);
  const [won, setWon] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [message, setMessage] = useState("");
  const [showCineWord, setShowCineWord] = useState(false);
  const [movieDetails, setMovieDetails] = useState(null);
  const [loadingClues, setLoadingClues] = useState(true);

  const hints = useMemo(
    () => buildHints(dailyMovie, movieDetails),
    [dailyMovie, movieDetails]
  );

  const suggestions = useMemo(() => {
    const search = normaliseTitle(guessInput);

    if (!search) return [];

    return cineWordMovies
      .filter((movie) =>
        normaliseTitle(movie.title).includes(search)
      )
      .slice(0, 7);
  }, [guessInput]);

  useEffect(() => {
    let cancelled = false;

    async function loadMovieDetails() {
      const apiKey = process.env.REACT_APP_OMDB_KEY;

      if (!apiKey) {
        console.error(
          "CineWord could not load clues because REACT_APP_OMDB_KEY is missing."
        );

        if (!cancelled) {
          setMessage("Today's detailed clues could not be loaded.");
          setLoadingClues(false);
        }

        return;
      }

      try {
        const parameters = new URLSearchParams({
          apikey: apiKey,
          t: dailyMovie.title,
          y: String(dailyMovie.year),
          plot: "short",
        });

        const response = await fetch(
          `https://www.omdbapi.com/?${parameters.toString()}`
        );

        if (!response.ok) {
          throw new Error(
            `OMDb request failed with status ${response.status}.`
          );
        }

        const data = await response.json();

        if (data.Response === "False") {
          throw new Error(data.Error || "Movie details were not found.");
        }

        if (!cancelled) {
          setMovieDetails(data);
        }
      } catch (error) {
        console.error("Could not load CineWord clues:", error);

        if (!cancelled) {
          setMessage(
            "Today's detailed clues could not be loaded."
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingClues(false);
        }
      }
    }

    loadMovieDetails();

    return () => {
      cancelled = true;
    };
  }, [dailyMovie]);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return undefined;
    }

    const progressRef = doc(
      db,
      "users",
      user.uid,
      "movieWordle",
      dateKey
    );

    return onSnapshot(
      progressRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();

          setGuesses(
            Array.isArray(data.guesses) ? data.guesses : []
          );
          setFinished(data.finished === true);
          setWon(data.won === true);
        } else {
          setGuesses([]);
          setFinished(false);
          setWon(false);
        }

        setLoading(false);
      },
      (error) => {
        console.error(
          "Could not load CineWord progress:",
          error
        );
        setMessage("Your progress could not be loaded.");
        setLoading(false);
      }
    );
  }, [user?.uid, dateKey]);

  async function saveProgress(
    nextGuesses,
    nextFinished,
    nextWon
  ) {
    if (!user?.uid) return;

    const progressRef = doc(
      db,
      "users",
      user.uid,
      "movieWordle",
      dateKey
    );

    await setDoc(
      progressRef,
      {
        dateKey,
        puzzleNumber: dailyMovie.puzzleNumber,
        guesses: nextGuesses,
        finished: nextFinished,
        won: nextWon,
        updatedAt: serverTimestamp(),
        ...(nextFinished
          ? { completedAt: serverTimestamp() }
          : {}),
      },
      { merge: true }
    );
  }

  async function submitGuess(event) {
    event?.preventDefault();

    if (finished) return;

    const selectedMovie = cineWordMovies.find(
      (movie) =>
        normaliseTitle(movie.title) ===
        normaliseTitle(guessInput)
    );

    if (!selectedMovie) {
      setMessage("Choose a film from the suggestions.");
      return;
    }

    const hasAlreadyBeenGuessed = guesses.some(
      (guess) =>
        normaliseTitle(guess.title) ===
        normaliseTitle(selectedMovie.title)
    );

    if (hasAlreadyBeenGuessed) {
      setMessage("You already guessed that film.");
      return;
    }

    const isCorrect =
      normaliseTitle(selectedMovie.title) ===
      normaliseTitle(dailyMovie.title);

    const nextGuesses = [
      ...guesses,
      {
        title: selectedMovie.title,
        year: selectedMovie.year,
        correct: isCorrect,
      },
    ];

    const nextFinished =
      isCorrect || nextGuesses.length >= MAX_GUESSES;

    setGuesses(nextGuesses);
    setWon(isCorrect);
    setFinished(nextFinished);
    setGuessInput("");
    setShowSuggestions(false);
    setMessage("");

    try {
      await saveProgress(
        nextGuesses,
        nextFinished,
        isCorrect
      );
    } catch (error) {
      console.error(
        "Could not save CineWord progress:",
        error
      );

      setMessage(
        "Your guess was recorded here, but cloud saving failed."
      );
    }
  }

  async function shareResult() {
    const blocks = guesses
      .map((guess) => (guess.correct ? "🟩" : "⬛"))
      .join("");

    const result = [
      `CineWord #${dailyMovie.puzzleNumber}`,
      won
        ? `${guesses.length}/${MAX_GUESSES}`
        : `X/${MAX_GUESSES}`,
      blocks,
      "",
      "Cineaste",
    ].join("\n");

    try {
      if (navigator.share) {
        await navigator.share({ text: result });
      } else {
        await navigator.clipboard.writeText(result);
        setMessage("Result copied.");
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error(
          "Could not share CineWord result:",
          error
        );
      }
    }
  }

  const revealedHints = Math.min(
    guesses.length + 1,
    hints.length
  );

  if (loading) {
    return (
      <div className="cineword-card">
        Loading today's CineWord…
      </div>
    );
  }

  return (
    <section className="cineword-card">
      <div className="cineword-heading">
        <div>
          <p className="cineword-kicker">
            Daily film challenge
          </p>
          <h3>CineWord</h3>
        </div>

        <div className="cineword-heading-actions">
          

          <button
            type="button"
            className="radio-next-button cineword-toggle"
            onClick={() =>
              setShowCineWord((current) => !current)
            }
          >
            {showCineWord ? "Hide game" : "Show game"}
          </button>
        </div>
      </div>

      {showCineWord && (
        <>
          <p className="cineword-intro">
            Guess today's film in six tries. Each
            incorrect guess reveals another clue.
            Progress is saved to your Cineaste account.
          </p>

          <div
            className="cineword-hints"
            aria-live="polite"
          >
            {loadingClues ? (
              <div className="cineword-hint is-revealed">
                <span>Clues</span>
                <strong>
                  Preparing today's clues…
                </strong>
              </div>
            ) : (
              hints.map((hint, index) => (
                <div
                  className={`cineword-hint ${
                    index < revealedHints || finished
                      ? "is-revealed"
                      : ""
                  }`}
                  key={`${index}-${hint}`}
                >
                  <span>Clue {index + 1}</span>

                  <strong>
                    {index < revealedHints || finished
                      ? hint
                      : "Locked"}
                  </strong>
                </div>
              ))
            )}
          </div>

          <div className="cineword-guesses">
            {Array.from({
              length: MAX_GUESSES,
            }).map((_, index) => {
              const guess = guesses[index];

              return (
                <div
                  className={`cineword-guess-row ${
                    guess?.correct ? "is-correct" : ""
                  }`}
                  key={index}
                >
                  <span className="cineword-attempt">
                    {index + 1}
                  </span>

                  <span className="cineword-guess-title">
                    {guess ? guess.title : "—"}
                  </span>

                  <span className="cineword-guess-year">
                    {guess ? guess.year : ""}
                  </span>
                </div>
              );
            })}
          </div>

          {!finished ? (
            <form
              className="cineword-form"
              onSubmit={submitGuess}
            >
              <div className="cineword-search-wrap">
                <input
                  value={guessInput}
                  onChange={(event) => {
                    setGuessInput(event.target.value);
                    setShowSuggestions(true);
                    setMessage("");
                  }}
                  onFocus={() =>
                    setShowSuggestions(true)
                  }
                  placeholder="Search and select a movie title"
                  aria-label="Movie title"
                  autoComplete="off"
                />

                {showSuggestions &&
                  suggestions.length > 0 && (
                    <div className="cineword-suggestions">
                      {suggestions.map((movie) => (
                        <button
                          type="button"
                          key={`${movie.title}-${movie.year}`}
                          onClick={() => {
                            setGuessInput(movie.title);
                            setShowSuggestions(false);
                          }}
                        >
                          <span>{movie.title}</span>
                          <small>{movie.year}</small>
                        </button>
                      ))}
                    </div>
                  )}
              </div>

              <button
                className="cineword-submit"
                type="submit"
                disabled={!guessInput.trim()}
              >
                Guess
              </button>
            </form>
          ) : (
            <div className="cineword-finished">
              <p>
                {won
                  ? `Correct — you found ${
                      dailyMovie.title
                    } in ${guesses.length} ${
                      guesses.length === 1
                        ? "guess"
                        : "guesses"
                    }.`
                  : `Today's film was ${dailyMovie.title} (${dailyMovie.year}).`}
              </p>

              <button
                type="button"
                onClick={shareResult}
              >
                Share result
              </button>
            </div>
          )}

          {message && (
            <p className="cineword-message">
              {message}
            </p>
          )}

          <p className="cineword-footer">
            A new film appears each day at 12:00 a.m.
            UTC.
          </p>
        </>
      )}
    </section>
  );
}
